import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useLicense } from '../context/LicenseContext';
import { UserRole } from '../types/user';

type LicenseFeature = 'telehealth';

type FeatureCheckers = {
  [key in LicenseFeature]: (entitlements: ReturnType<typeof useLicense>) => boolean;
};

const featureChecks: FeatureCheckers = {
  telehealth: (entitlements) =>
    entitlements.telehealthEnabled || entitlements.featureFlags.telehealth === true,
};

const getGraceCopy = (graceExpires: string | null): string | null => {
  if (!graceExpires) {
    return null;
  }

  const graceDate = new Date(graceExpires);

  if (Number.isNaN(graceDate.getTime())) {
    return null;
  }

  return graceDate.toLocaleDateString();
};

const featureFallbackCopy: Record<LicenseFeature, { title: string; body: string }> = {
  telehealth: {
    title: 'Telehealth Not Available',
    body: 'Telehealth scheduling is not included with your current plan.',
  },
};

// HA-263: Patient-facing copy for license-gated features. Patients don't
// have a "plan" — their practitioner does — so the practitioner-authored
// upgrade pitch doesn't apply. Defense in depth in case a Client lands on
// any license-gated route directly.
const clientFallbackCopy: Record<LicenseFeature, { title: string; body: string }> = {
  telehealth: {
    title: "Video sessions aren't available",
    body: "Your practitioner hasn't enabled video sessions for your practice. Use Messages to connect with them.",
  },
};

interface LicenseGuardProps {
  feature: LicenseFeature;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

const PractitionerFallback: React.FC<{
  feature: LicenseFeature;
  tier: string | null;
  graceExpires: string | null;
}> = ({ feature, tier, graceExpires }) => {
  const copy = featureFallbackCopy[feature];
  const graceCopy = getGraceCopy(graceExpires);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        {copy.title}
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {copy.body} {tier ? `Current tier: ${tier}.` : ''}
        {graceCopy ? ` Grace period ends ${graceCopy}.` : ''}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        Visit subscription settings to review upgrade options or contact your administrator for more
        access.
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Button component={Link} to="/subscription" variant="contained">
          Manage Subscription
        </Button>
      </Box>
    </Box>
  );
};

const ClientFallback: React.FC<{ feature: LicenseFeature }> = ({ feature }) => {
  const copy = clientFallbackCopy[feature];

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        {copy.title}
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        {copy.body}
      </Typography>
      <Button component={Link} to="/Messaging" variant="contained">
        Open Messages
      </Button>
    </Box>
  );
};

const LicenseGuard: React.FC<LicenseGuardProps> = ({ feature, fallback, children }) => {
  const entitlements = useLicense();
  const { currentUser } = useAuth();
  const isAllowed = featureChecks[feature](entitlements);

  if (isAllowed) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  if (currentUser?.role === UserRole.Client) {
    return <ClientFallback feature={feature} />;
  }

  return (
    <PractitionerFallback
      feature={feature}
      tier={entitlements.tier}
      graceExpires={entitlements.graceExpires}
    />
  );
};

export default LicenseGuard;
